export type DateRange = { startDate: string; endDate: string };
export const emptyDateDuration: DateDurationInput = { days: '', months: '', years: '' };
export const emptyDateRange: DateRange = { startDate: '', endDate: '' };
export const validDate = (date: string) => /^\d{4}-\d{2}-\d{2}$/.test(date) && Number.isFinite(Date.parse(date)) && new Date(date).toISOString().slice(0, 10) === date;
export function calendarDaySpan(range: DateRange) {
  return validDate(range.startDate) && validDate(range.endDate) && range.startDate <= range.endDate
    ? Math.round((Date.parse(range.endDate) - Date.parse(range.startDate)) / 86400000) : -1;
}
export function validRange(range: DateRange) { const span = calendarDaySpan(range); return span >= 0 && span <= 3660; }
export const dateLabel = (date: string) => new Date(date + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
export function unionDateRanges(ranges: Array<Partial<DateRange> | null | undefined>): DateRange | null {
  const starts = ranges.map(range => range?.startDate || '').filter(validDate).sort();
  const ends = ranges.map(range => range?.endDate || '').filter(validDate).sort();
  return starts.length && ends.length ? { startDate: starts[0], endDate: ends[ends.length - 1] } : null;
}
export type DateRangeChoice = DateRangePreset | 'all' | 'custom' | 'duration';

export type DateRangeAnchor = 'first-data' | 'latest-data';
export const dateRangePresets = [
  { value: '7d', label: '7 days', days: 7 },
  { value: '14d', label: '14 days', days: 14 },
  { value: '1m', label: '1 month', months: 1 },
  { value: '3m', label: '3 months', months: 3 },
  { value: '6m', label: '6 months', months: 6 },
  { value: '12m', label: '12 months', months: 12 },
] as const;
export type DateRangePreset = typeof dateRangePresets[number]['value'];
export type DateDuration = { days: number; months: number; years: number };
export type DateDurationInput = { [Unit in keyof DateDuration]: string };

export function presetDateRange(anchor: DateRangeAnchor, preset: DateRangePreset,
  available: DateRange | null): DateRange | null {
  const option = dateRangePresets.find(item => item.value === preset);
  if (!option) return null;
  return durationDateRange(anchor, {
    days: 'days' in option ? option.days : 0,
    months: 'months' in option ? option.months : 0, years: 0,
  }, available);
}

export function durationDateRange(anchor: DateRangeAnchor, duration: DateDuration,
  available: DateRange | null): DateRange | null {
  if (!Object.values(duration).every(value => Number.isSafeInteger(value) && value >= 0)) return null;
  const months = duration.years * 12 + duration.months;
  if (months > 120 || duration.days > 3661 || months + duration.days === 0) return null;
  const date = anchor === 'first-data' ? available?.startDate : available?.endDate;
  if (!date || !validRange({ startDate: date, endDate: date })) return null;
  const direction = anchor === 'first-data' ? 1 : -1;
  const target = new Date(date + 'T00:00:00Z');
  if (months) {
    // Calendar months preserve the day where possible; Jan 31 + 1 month is
    // Feb 28/29, never March. Both displayed endpoints remain included.
    const day = target.getUTCDate();
    target.setUTCDate(1);
    target.setUTCMonth(target.getUTCMonth() + direction * months);
    const monthEnd = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(day, monthEnd));
  }
  // Day-only durations count both endpoints, matching the 7/14-day presets.
  // In a combined duration, days extend the calendar-month/year boundary.
  target.setUTCDate(target.getUTCDate() + direction * (months ? duration.days : duration.days - 1));
  if (!Number.isFinite(target.getTime())) return null;
  const shifted = target.toISOString().slice(0, 10);
  const range = anchor === 'first-data' ? { startDate: date, endDate: shifted } : { startDate: shifted, endDate: date };
  return validRange(range) ? range : null;
}
